import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiJson } from '../api';
import { Shell } from '../components/Shell';
import './ExplorePage.css';
import { useMeProfile } from '../utils/useMeProfile';
import { moduleScoreForProfile } from '../utils/whyPersonalization';
import {
  ExploreClusterCarousel,
  type ExploreModule,
  type ExploreRes,
  moduleHasTopic,
} from './exploreClusters';

export function ExplorePage() {
  const location = useLocation();
  const [data, setData] = useState<ExploreRes | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [continueItem, setContinueItem] = useState<{ slug: string; title: string; at: number } | null>(null);
  const { explicit } = useMeProfile();

  const adaptEnabled = (explicit?.adaptRecommendations ?? true) !== false;

  useEffect(() => {
    try {
      const raw = localStorage.getItem('wi_explore_last');
      if (raw) {
        const parsed = JSON.parse(raw) as { slug?: string; title?: string; at?: number };
        if (parsed.slug && parsed.title) setContinueItem({ slug: parsed.slug, title: parsed.title, at: parsed.at ?? 0 });
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiJson<ExploreRes>('/api/explore?limit=400');
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const applyParallax = () => {
      raf = 0;
      const y = window.scrollY || window.pageYOffset || 0;
      const drift = Math.min(42, y * 0.06);
      const driftSoft = Math.min(26, y * 0.035);
      document.documentElement.style.setProperty('--wi-explore-parallax-y', `${drift.toFixed(2)}px`);
      document.documentElement.style.setProperty('--wi-explore-parallax-y-soft', `${driftSoft.toFixed(2)}px`);
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(applyParallax);
    };
    applyParallax();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
      document.documentElement.style.removeProperty('--wi-explore-parallax-y');
      document.documentElement.style.removeProperty('--wi-explore-parallax-y-soft');
    };
  }, []);

  useEffect(() => {
    const hash = location.hash?.replace(/^#/, '');
    if (!hash || !data) return;
    const el = document.getElementById(hash);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [data, location.pathname, location.hash]);

  const modulesByTopic = useMemo(() => {
    const map = new Map<string, ExploreModule[]>();
    for (const topic of data?.topics ?? []) {
      const arr = (data?.modules ?? []).filter((m) => moduleHasTopic(m, topic.slug));
      if (adaptEnabled && explicit) {
        arr.sort((a, b) => moduleScoreForProfile(b, 'foundations', explicit) - moduleScoreForProfile(a, 'foundations', explicit));
      } else {
        arr.sort((a, b) => a.title.localeCompare(b.title));
      }
      map.set(topic.slug, arr);
    }
    return map;
  }, [data?.topics, data?.modules, adaptEnabled, explicit]);

  const showContinue =
    continueItem &&
    continueItem.slug &&
    Date.now() - continueItem.at < 1000 * 60 * 60 * 24 * 14;

  return (
    <Shell>
      <div className="wi-explore wi-explore--nizam">
        <header className="wi-explore-header wi-explore-header--home-match" aria-labelledby="wi-explore-hero-title">
          <div className="wi-explore-inner wi-hero-inner">
            <div className="wi-explore-hero-copy wi-hero-copy">
              <p className="wi-mk-kicker">Structured inquiry</p>
              <h1 id="wi-explore-hero-title" className="wi-explore-title">
                Big Ideas
              </h1>
              <p className="wi-explore-lede wi-hero-lede">
                Move through Islam as a structured landscape—domain by domain—with curated clusters that surface depth, not a scattered feed.
              </p>
              {showContinue ? (
                <Link to={`/m/${continueItem!.slug}`} className="wi-hero-cta-pill">
                  {continueItem!.title}
                </Link>
              ) : (
                <Link to="/journeys#wi-journey-scenes" className="wi-hero-cta-pill">
                  Start exploring
                </Link>
              )}
            </div>
          </div>
        </header>

        {error ? <p className="wi-explore-error">{error}</p> : null}
        {!data && !error ? (
          <div className="wi-explore-inner wi-explore-loading-block" aria-busy aria-label="Loading Big Ideas">
            <div className="wi-explore-skeleton-grid">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="wi-explore-skeleton wi-explore-skeleton--card" />
              ))}
            </div>
          </div>
        ) : null}

        {data
          ? data.topics.map((topic, idx) => {
              const mods = modulesByTopic.get(topic.slug) ?? [];
              if (!mods.length) return null;
              return (
                <ExploreClusterCarousel
                  key={topic.slug}
                  topic={topic}
                  sectionIndex={idx + 1}
                  mods={mods}
                  explicitProfile={explicit}
                  adaptEnabled={adaptEnabled}
                />
              );
            })
          : null}
      </div>
    </Shell>
  );
}
